import styled, { css } from 'styled-components';

import { customScrollbar, hiddenScrollbar } from '@/styles/style';
import zIndex from '@/styles/zIndex';

type DividerProps = {
  direction?: 'v' | 'h';
  backgroundColor?: string;
  length?: number;
};

export const convertPxProps = (
  defaultValue?: string | number,
  v?: string | number,
) => {
  if (typeof v === 'number') return `${v}px`;
  if (typeof v === 'string') return v;
  return defaultValue;
};

export const Divider = styled.div<DividerProps>`
  ${({ direction = 'h', length }) =>
    direction === 'h'
      ? `width: ${length ? `${length}px` : '100%'}; height: 1px`
      : `width: 1px; height: ${length ? `${length}px` : 'auto'};`};
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor ? backgroundColor : theme.colors.border};
  flex-shrink: 0;
`;

type SpaceProps = {
  w?: string | number;
  h?: string | number;
};

export const Space = styled.div<SpaceProps>`
  width: ${({ w }) => convertPxProps('100%', w)};
  height: ${({ h }) => convertPxProps('1px', h)};
  flex-shrink: 0;
`;

export type JustifyContentProps =
  | 'inherit'
  | 'initial'
  | 'normal'
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'left'
  | 'right'
  | 'start'
  | 'end'
  | 'stretch'
  | 'unset';

export type AlignItemsProps =
  | 'inherit'
  | 'initial'
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'start'
  | 'end'
  | 'normal'
  | 'stretch'
  | 'baseline'
  | 'unset';

export type DirectionProps =
  | 'row'
  | 'row-reverse'
  | 'column'
  | 'column-reverse';

export type WrapProps = 'wrap' | 'nowrap' | 'wrap-reverse';

export type FlexProps = React.HTMLProps<HTMLDivElement> & FlexShorthandProps;

export type FlexShorthandProps = {
  j?: JustifyContentProps;
  a?: AlignItemsProps;
  d?: DirectionProps;
  g?: string | number;
  w?: WrapProps;
  f?: string | number;
  width?: string;
  height?: string;
  padding?: string;
  css?: StyledCss;
  overflow?: string;
};

export const Flex = styled.div<FlexProps>`
  display: flex;
  justify-content: ${({ j }) => j};
  align-items: ${({ a }) => a};
  flex-direction: ${({ d }) => d};
  gap: ${({ g }) => convertPxProps(undefined, g)};
  flex-wrap: ${({ w }) => w};
  flex: ${({ f }) => f};
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  padding: ${({ padding }) => padding};
  ${({ css }) => css};
  overflow: ${({ overflow }) => overflow};
`;

export const InlineFlex = styled(Flex)`
  display: inline-flex;
`;

export const CenterFlex = styled(Flex).attrs({
  a: 'center',
  j: 'center',
})``;

export const VFlex = styled(Flex).attrs({
  d: 'column',
})``;

export const ScrollVFlex = styled(VFlex)`
  overflow: auto;
  ${customScrollbar};
`;

export const DropdownBackground = styled.div`
  position: fixed;
  z-index: ${zIndex.dim};
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  cursor: unset;
`;

export const IconButtonStyle = styled(Flex).attrs({
  a: 'center',
  j: 'center',
})`
  height: 24px;
  width: 24px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  color: ${(props) => props.theme.colors.gray500};
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
`;

type ShortCutStyleProps = {
  outlined?: boolean;
};

export const ShortcutStyle = styled.span<ShortCutStyleProps>`
  padding: 4px 4px;
  background-color: ${(props) => props.theme.colors.gray100};
  color: ${(props) => props.theme.colors.gray800};
  min-width: 18px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 2px;
  line-height: 1;
  ${({ outlined }) =>
    outlined &&
    css`
      background-color: transparent;
      border: 1px solid ${(props) => props.theme.colors.gray400};
      color: ${(props) => props.theme.colors.gray500};
      border-radius: 4px;
    `};
`;

type ScrollProps = {
  hideScrollbar?: boolean;
};

export const HorizontalScrollView = styled(Flex)<ScrollProps>`
  position: relative;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  white-space: nowrap;
  scroll-behavior: smooth;
  ${({ hideScrollbar }) => (hideScrollbar ? hiddenScrollbar : customScrollbar)}
`;

export const Sticky = styled.div`
  position: sticky;
  top: 0;
`;

type GridProps = React.HTMLProps<HTMLDivElement> & {
  columns?: string;
  gap?: string;
};

export const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  gap: ${(props) => props.gap};
`;
